import { Link, NavLink, useParams } from 'react-router-dom'

import {
  ArrowUpRightIcon,
  Badge,
  Button,
  Card,
  ChevronLeftIcon,
  Flex,
  Text,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import {
  IntegrationDetailPage,
  IntegrationIcon,
} from './components/IntegrationDetailDialog'
import { EmailVerificationGate } from '../shared/components/EmailVerificationGate'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { useIntegrationConnections } from '../shared/hooks/useIntegrationConnections'
import { INTEGRATIONS } from '../shared/integrations/catalog'
import type { Integration, IntegrationId } from '../shared/integrations/types'

import detailStyles from './components/IntegrationDetailDialog/IntegrationDetailPage.module.css'
import styles from './Integrations.module.css'

import type { JSX } from 'react'

const INTEGRATIONS_BY_ID = Object.fromEntries(
  INTEGRATIONS.map((i) => [i.id, i]),
)

function isIntegrationId(id: string): id is IntegrationId {
  return id in INTEGRATIONS_BY_ID
}

function makeIconStyle(
  bgColor: string,
): React.CSSProperties & Record<`--${string}`, string> {
  return { '--card-icon-bg': bgColor }
}

const IntegrationCardContent = ({
  integration,
  isConnected,
}: {
  integration: Integration
  isConnected: boolean
}): JSX.Element => (
  <>
    <div
      className={styles.cardIcon}
      style={
        integration.iconSrc ? undefined : makeIconStyle(integration.iconBgColor)
      }
    >
      {integration.iconSrc ? (
        <img
          src={integration.iconSrc}
          alt=""
          className={styles.cardIconImage}
        />
      ) : (
        integration.iconLetter
      )}
    </div>
    <div className={styles.cardContent}>
      <div className={styles.cardHeader}>
        <Text as="span" weight="medium" className={styles.cardName}>
          {integration.name}
        </Text>
        {integration.comingSoon && (
          <Badge variant="neutral" size="small">
            Coming soon
          </Badge>
        )}
        {isConnected && (
          <Badge variant="brand-subtle" size="small">
            Connected
          </Badge>
        )}
      </div>
      <Text as="p" color="subtle" className={styles.cardDescription}>
        {integration.description}
      </Text>
      {integration.interestFormUrl && (
        <Text
          as="span"
          size="sm"
          color="brand"
          icon={<ArrowUpRightIcon size="small" />}
          iconPosition="end"
        >
          Get early access
        </Text>
      )}
    </div>
  </>
)

const IntegrationGrid = (): JSX.Element => {
  const organizationId = useOrganizationId()
  const connectionsByIntegration = useIntegrationConnections()

  const trackClick = (integration: Integration): void => {
    BufferTracker.mCPIntegrationSelected({
      organizationId: organizationId ?? '',
      integrationId: integration.id,
      integrationName: integration.name,
      clientName: Client.PublishWeb,
      source: 'integrations_page',
    })
  }

  return (
    <div className={styles.grid}>
      {INTEGRATIONS.map((integration) => {
        const isConnected = connectionsByIntegration.has(integration.id)
        const isDisabled =
          integration.comingSoon && !integration.interestFormUrl

        if (isDisabled) {
          return (
            <Card
              key={integration.id}
              padding="lg"
              className={`${styles.card} ${styles.cardDisabled}`}
              aria-disabled
            >
              <IntegrationCardContent
                integration={integration}
                isConnected={isConnected}
              />
            </Card>
          )
        }

        if (integration.comingSoon && integration.interestFormUrl) {
          return (
            <Card
              key={integration.id}
              padding="lg"
              className={`${styles.card} ${styles.cardClickable}`}
              asChild
            >
              <a
                href={integration.interestFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick(integration)}
              >
                <IntegrationCardContent
                  integration={integration}
                  isConnected={isConnected}
                />
              </a>
            </Card>
          )
        }

        return (
          <Card
            key={integration.id}
            padding="lg"
            className={`${styles.card} ${styles.cardClickable}`}
            asChild
          >
            <Link
              to={`/settings/integrations/${integration.id}`}
              onClick={() => trackClick(integration)}
            >
              <IntegrationCardContent
                integration={integration}
                isConnected={isConnected}
              />
            </Link>
          </Card>
        )
      })}
    </div>
  )
}

const Integrations = (): JSX.Element => {
  const { integrationId } = useParams<{ integrationId: string }>()

  const selectedIntegration =
    integrationId && isIntegrationId(integrationId)
      ? INTEGRATIONS_BY_ID[integrationId]
      : null

  if (selectedIntegration) {
    return (
      <NewSettingsPageLayout
        title={
          <Flex direction="column" gap="md">
            <Button
              variant="tertiary"
              size="small"
              as={NavLink}
              to="/settings/integrations"
              className={detailStyles.backButton}
            >
              <ChevronLeftIcon />
              Integrations
            </Button>
            <Flex align="center" gap="md">
              <IntegrationIcon integration={selectedIntegration} />
              <Flex direction="column">
                <span>{selectedIntegration.name}</span>
                <Text color="subtle">{selectedIntegration.description}</Text>
              </Flex>
            </Flex>
          </Flex>
        }
        pageTitle={`${selectedIntegration.name} Integration`}
        subtitle=""
      >
        <EmailVerificationGate>
          <IntegrationDetailPage integration={selectedIntegration} />
        </EmailVerificationGate>
      </NewSettingsPageLayout>
    )
  }

  return (
    <NewSettingsPageLayout
      title="Integrations"
      subtitle="Extend Buffer with agents and integrations."
    >
      <EmailVerificationGate>
        <IntegrationGrid />
      </EmailVerificationGate>
    </NewSettingsPageLayout>
  )
}

export default Integrations
